import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useId } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
  ActionSetId,
  ActionSetRef,
  CategoryId,
  ConditionalActionMatcherDto,
  DateString,
  EditActionDto,
  ExpenseTypeId,
  FieldName,
  GroupId,
  Money,
  SourceId,
  TextFieldName,
} from "../../api/types";
import { makeDateString } from "../../api/types";
import { useActionSets } from "../../api/queries";
import { expandActions } from "../../logic/action-set-template";
import { summarizeActions } from "../../logic/auto-transaction-summary";
import { useRuleSetNames } from "../../hooks/useRuleSetNames";
import {
  CategoryPickerField,
  ExpenseTypePickerField,
  GroupPickerField,
  SourcePickerField,
} from "../EntityPickerField";
import { DatePicker } from "../DatePicker";
import { RetiredFieldChip } from "./RetiredFieldChip";
import {
  COPYABLE_FROM_FIELDS,
  EMPTY_ID,
  FIELD_LABELS,
  PREFIXABLE_FIELDS,
  PROMPTABLE_FIELDS,
  defaultActionForField,
  MATCHER_LABELS,
} from "./constants";
import { AmountActionField } from "./AmountActionField";
import { MatchActionEditor } from "./MatchActionEditor";
import { TemplateTextField } from "./TemplateTextField";

// Action Editor — pickers source/return ids straight to/from action values,
// so no entity registry threading is needed here.
/**
 * An action that defers to a named action set. The set's own actions are shown
 * read-only underneath so the rule reads end to end, but are not editable here:
 * changing them would change every rule citing the set, which is exactly the
 * decision this indirection exists to keep deliberate.
 *
 * What *is* editable is the set's inputs — the holes it deliberately left for
 * each citing rule. The preview underneath renders with those values already
 * substituted, so the read-only block shows what this rule will do rather than
 * what the set says in the abstract.
 */
const ActionSetRefEditor = ({
  value,
  onChange,
  templateInputs,
}: {
  value: ActionSetRef;
  onChange: (a: EditActionDto) => void;
  /** The scope the citing action sits in — see `ActionEditor`. */
  templateInputs: readonly string[] | null;
}) => {
  const labelId = useId();
  const { data: sets } = useActionSets();
  const resolvers = useRuleSetNames();
  const selected = sets?.find((s) => s.id === value.set);

  // One box per input the set declares, in the set's order, seeded from what
  // the citation already carries. Values left over from a renamed input are
  // dropped here, the same way expansion drops them.
  const inputs = (selected?.inputs ?? []).map((input) => ({
    name: input.name,
    description: input.description,
    value: value.inputs.find((i) => i.name === input.name)?.value ?? "",
  }));

  const pickSet = (set: ActionSetId) => {
    const target = sets?.find((s) => s.id === set);
    onChange({
      field: "applyActionSet",
      value: {
        set,
        inputs: (target?.inputs ?? []).map((input) => ({
          name: input.name,
          value: "",
        })),
      },
    });
  };

  const setInput = (name: string, next: string) =>
    onChange({
      field: "applyActionSet",
      value: {
        set: value.set,
        inputs: inputs.map((input) => ({
          name: input.name,
          value: input.name === name ? next : input.value,
        })),
      },
    });

  const preview = selected
    ? summarizeActions(
        expandActions([{ field: "applyActionSet", value }], sets),
        resolvers,
      ).full
    : null;

  return (
    <Box>
      <FormControl size="small" fullWidth>
        <InputLabel id={labelId}>Shared actions</InputLabel>
        <Select
          labelId={labelId}
          label="Shared actions"
          value={sets ? value.set : ""}
          onChange={(e) => pickSet(e.target.value as ActionSetId)}
        >
          {(sets ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {inputs.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {inputs.map((input) => (
            <TemplateTextField
              key={input.name}
              label={input.name}
              value={input.value}
              onChange={(next) => setInput(input.name, next)}
              helperText={input.description ?? undefined}
              // The value is written where the citation sits, not inside the
              // cited set: the cited set's own inputs are never in scope here.
              // Cited from an action set, `{Navn}` is that enclosing set's
              // input, passed through; cited from a rule, there are no inputs
              // at all and only a `{$field}` means anything.
              inputNames={templateInputs}
            />
          ))}
        </Stack>
      )}
      <Typography
        variant="caption"
        component="pre"
        sx={{
          color: "text.secondary",
          m: 0,
          mt: 0.5,
          pl: 1,
          whiteSpace: "pre-wrap",
          borderLeft: "2px solid",
          borderColor: "divider",
        }}
      >
        {preview ??
          (value.set
            ? "This action set no longer exists — the rule cannot run until this is fixed."
            : "Pick an action set.")}
      </Typography>
    </Box>
  );
};

export const ActionEditor = ({
  action,
  onChange,
  onRemove,
  isMobile,
  isNested = false,
  templateInputs = null,
}: {
  action: EditActionDto;
  onChange: (a: EditActionDto) => void;
  onRemove: () => void;
  isMobile: boolean;
  isNested?: boolean;
  /**
   * The names a bare `{Navn}` may refer to: the enclosing action set's inputs
   * when editing a set, `null` in a rule's own action list, where rules have
   * no inputs and only `{$field}` means anything.
   */
  templateInputs?: readonly string[] | null;
}) => {
  // Labels have to be tied to their Select by id, or the pair reads as two
  // unnamed dropdowns to a screen reader.
  const copyIds = useId();
  const prompt = action.field === "prompt" ? action.value : null;
  const copyField = action.field === "copyField" ? action.value : null;
  const actionSetRef = action.field === "applyActionSet" ? action.value : null;
  const isPrompt = prompt !== null;
  const dropdownField: string = prompt ? prompt.field : action.field;
  const promptableField: FieldName | null =
    isPrompt || PROMPTABLE_FIELDS.has(dropdownField as FieldName)
      ? (dropdownField as FieldName)
      : null;

  const handleFieldChange = (newField: string) => {
    if (prompt && PROMPTABLE_FIELDS.has(newField as FieldName)) {
      // Carry the prefix across a switch between two text fields; drop it when
      // the new field has no text box to seed.
      onChange({
        field: "prompt",
        value: {
          field: newField as FieldName,
          prefix: PREFIXABLE_FIELDS.has(newField as FieldName)
            ? prompt.prefix
            : null,
        },
      });
      return;
    }
    if (PROMPTABLE_FIELDS.has(newField as FieldName)) {
      onChange(defaultActionForField(newField as FieldName));
      return;
    }
    switch (newField) {
      case "addCategory":
        onChange({ field: "addCategory", value: [] });
        break;
      case "removeCategory":
        onChange({ field: "removeCategory", value: [] });
        break;
      case "applyActionSet":
        // Blank until picked; the backend rejects a reference to a set that
        // doesn't exist, so an unpicked one can't be saved by mistake.
        onChange({
          field: "applyActionSet",
          value: { set: EMPTY_ID as ActionSetId, inputs: [] },
        });
        break;
      case "copyField":
        onChange({
          field: "copyField",
          value: { from: "source", to: "description" },
        });
        break;
      case "conditional":
        onChange({
          field: "conditional",
          value: {
            condition: { field: "source", value: [] },
            action: { field: "description", value: "" },
          },
        });
        break;
      case "match":
        // One empty arm to start: a match with no arms at all is refused on
        // save, and an empty card gives nothing to fill in.
        onChange({
          field: "match",
          value: {
            arms: [
              {
                condition: { field: "source", value: [] },
                action: { field: "description", value: "" },
              },
            ],
          },
        });
        break;
    }
  };

  const handleAskUserToggle = (next: boolean) => {
    if (!promptableField) return;
    if (next) {
      onChange({
        field: "prompt",
        value: { field: promptableField, prefix: null },
      });
    } else {
      onChange(defaultActionForField(promptableField));
    }
  };

  if (action.field === "retired") {
    // Read-only: there is no value to edit into. A top-level action is
    // removed; a nested one (the "Then" of a conditional or an arm) has no
    // row of its own to delete, so it is replaced with a blank action.
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <RetiredFieldChip field={action.value} />
        {isNested ? (
          <Button
            size="small"
            onClick={() => onChange({ field: "description", value: "" })}
          >
            Replace
          </Button>
        ) : (
          <IconButton size="small" onClick={onRemove} aria-label="remove">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    );
  }

  if (action.field === "match") {
    return (
      <MatchActionEditor
        value={action.value}
        onChange={onChange}
        onRemove={onRemove}
        isMobile={isMobile}
        templateInputs={templateInputs}
      />
    );
  }

  if (action.field === "conditional") {
    const c = action.value;
    const setCondition = (condition: ConditionalActionMatcherDto) =>
      onChange({ ...action, value: { ...c, condition } });
    // No bottom margin: spacing between actions belongs to the list that
    // stacks them.
    return (
      <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">Conditional Action</Typography>
          <IconButton size="small" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack spacing={2}>
          {/* "If" row */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1 : 2}
            sx={{
              alignItems: isMobile ? "stretch" : "center",
            }}
          >
            <Typography sx={{ minWidth: 40, fontWeight: "bold" }}>
              If
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              fullWidth={isMobile}
            >
              <Select
                value={c.condition.field}
                onChange={(e) =>
                  // Ids of one kind mean nothing to another, so switching the
                  // field starts the selection over.
                  setCondition({
                    field: e.target.value,
                    value: [],
                  } as ConditionalActionMatcherDto)
                }
              >
                <MenuItem value="source">Source</MenuItem>
                {/* Listed only while this conditional already dispatches on
                    a retired field, so the select can show it; picking any
                    other field is how it is fixed. */}
                {c.condition.field === "retired" && (
                  <MenuItem value="retired">{MATCHER_LABELS.retired}</MenuItem>
                )}
                <MenuItem value="group">Group</MenuItem>
                <MenuItem value="expenseType">Expense Type</MenuItem>
                <MenuItem value="category">Category</MenuItem>
              </Select>
            </FormControl>

            {/* Multi-select: the matcher means "one of these", and one pick is
                just a one-element list. */}
            <Box sx={{ width: "100%" }}>
              {c.condition.field === "source" && (
                <SourcePickerField
                  size="small"
                  multiple
                  label="Value"
                  value={c.condition.value}
                  onChange={(ids) =>
                    setCondition({ field: "source", value: ids })
                  }
                />
              )}
              {c.condition.field === "retired" && (
                <RetiredFieldChip field={c.condition.value} />
              )}
              {c.condition.field === "group" && (
                <GroupPickerField
                  size="small"
                  multiple
                  label="Value"
                  value={c.condition.value}
                  onChange={(ids) =>
                    setCondition({ field: "group", value: ids })
                  }
                />
              )}
              {c.condition.field === "expenseType" && (
                <ExpenseTypePickerField
                  size="small"
                  multiple
                  label="Value"
                  value={c.condition.value}
                  onChange={(ids) =>
                    setCondition({ field: "expenseType", value: ids })
                  }
                />
              )}
              {c.condition.field === "category" && (
                <CategoryPickerField
                  size="small"
                  multiple
                  label="Value"
                  value={c.condition.value}
                  onChange={(ids) =>
                    setCondition({ field: "category", value: ids })
                  }
                />
              )}
            </Box>
          </Stack>

          {/* "Then" row */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1 : 2}
            sx={{
              alignItems: isMobile ? "stretch" : "flex-start",
            }}
          >
            <Typography sx={{ minWidth: 40, fontWeight: "bold", mt: 1 }}>
              Then
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <ActionEditor
                action={c.action}
                onChange={(newInnerAction) => {
                  onChange({
                    ...action,
                    value: {
                      ...c,
                      action: newInnerAction,
                    },
                  });
                }}
                onRemove={() => {}}
                isMobile={isMobile}
                isNested={true}
                templateInputs={templateInputs}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      spacing={isMobile ? 1 : 2}
      sx={{
        alignItems: isMobile ? "stretch" : "center",
      }}
    >
      <FormControl size="small" sx={{ minWidth: 150 }} fullWidth={isMobile}>
        <InputLabel>Field</InputLabel>
        <Select
          value={dropdownField}
          label="Field"
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          <MenuItem value="source">Source</MenuItem>
          <MenuItem value="merchant">Merchant</MenuItem>
          <MenuItem value="group">Group</MenuItem>
          <MenuItem value="description">Description</MenuItem>
          <MenuItem value="amount">Amount</MenuItem>
          <MenuItem value="expenseType">Expense Type</MenuItem>
          <MenuItem value="datePaid">Date Paid</MenuItem>
          <MenuItem value="addCategory">Add Category</MenuItem>
          <MenuItem value="removeCategory">Remove Category</MenuItem>
          <MenuItem value="copyField">Copy Field</MenuItem>
          <MenuItem value="applyActionSet">Shared actions</MenuItem>
          {!isNested && [
            <MenuItem key="conditional" value="conditional">
              Conditional Action
            </MenuItem>,
            <MenuItem key="match" value="match">
              Match (no default)
            </MenuItem>,
          ]}
        </Select>
      </FormControl>
      {promptableField && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isPrompt}
              onChange={(e) => handleAskUserToggle(e.target.checked)}
            />
          }
          label="Ask user"
        />
      )}
      <Box sx={{ flexGrow: 1 }}>
        {actionSetRef !== null && (
          <ActionSetRefEditor
            value={actionSetRef}
            onChange={onChange}
            templateInputs={templateInputs}
          />
        )}
        {copyField && (
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1 : 2}
            sx={{ alignItems: isMobile ? "stretch" : "center" }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              fullWidth={isMobile}
            >
              <InputLabel id={`${copyIds}-from`}>From</InputLabel>
              <Select
                labelId={`${copyIds}-from`}
                value={copyField.from}
                label="From"
                onChange={(e) =>
                  onChange({
                    field: "copyField",
                    value: { ...copyField, from: e.target.value as FieldName },
                  })
                }
              >
                {COPYABLE_FROM_FIELDS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {FIELD_LABELS[f]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              fullWidth={isMobile}
            >
              <InputLabel id={`${copyIds}-into`}>Into</InputLabel>
              <Select
                labelId={`${copyIds}-into`}
                value={copyField.to}
                label="Into"
                onChange={(e) =>
                  onChange({
                    field: "copyField",
                    value: {
                      ...copyField,
                      to: e.target.value as TextFieldName,
                    },
                  })
                }
              >
                <MenuItem value="merchant">Merchant</MenuItem>
                <MenuItem value="description">Description</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
        {prompt && PREFIXABLE_FIELDS.has(prompt.field) && (
          <TemplateTextField
            label="Prefix (optional)"
            placeholder="Parkering - "
            helperText="Pre-fills the box you're asked to complete"
            value={prompt.prefix ?? ""}
            onChange={(value) =>
              onChange({
                field: "prompt",
                value: { field: prompt.field, prefix: value || null },
              })
            }
            inputNames={templateInputs}
          />
        )}
        {!isPrompt && dropdownField === "source" && (
          <SourcePickerField
            size="small"
            value={(action.value as SourceId) || null}
            onChange={(id) =>
              onChange({
                field: "source",
                value: (id ?? (EMPTY_ID as SourceId)) as SourceId,
              })
            }
          />
        )}
        {/* Both category actions take a list. `allowCreate` is spelled out
            because the picker defaults inline-create off in multi mode, and
            building a rule around a category you haven't made yet was already
            possible here. */}
        {!isPrompt && dropdownField === "addCategory" && (
          <CategoryPickerField
            size="small"
            multiple
            allowCreate
            value={action.value as CategoryId[]}
            onChange={(ids) => onChange({ field: "addCategory", value: ids })}
          />
        )}
        {!isPrompt && dropdownField === "removeCategory" && (
          <CategoryPickerField
            size="small"
            multiple
            allowCreate
            value={action.value as CategoryId[]}
            onChange={(ids) =>
              onChange({ field: "removeCategory", value: ids })
            }
          />
        )}
        {!isPrompt && dropdownField === "group" && (
          <GroupPickerField
            size="small"
            value={(action.value as GroupId) || null}
            onChange={(id) =>
              onChange({
                field: "group",
                value: (id ?? (EMPTY_ID as GroupId)) as GroupId,
              })
            }
          />
        )}
        {!isPrompt && dropdownField === "expenseType" && (
          <ExpenseTypePickerField
            size="small"
            value={(action.value as ExpenseTypeId) || null}
            onChange={(id) =>
              onChange({
                field: "expenseType",
                value: (id ?? (EMPTY_ID as ExpenseTypeId)) as ExpenseTypeId,
              })
            }
          />
        )}
        {!isPrompt && dropdownField === "merchant" && (
          <TemplateTextField
            value={action.value as string}
            onChange={(value) => onChange({ field: "merchant", value })}
            inputNames={templateInputs}
          />
        )}
        {!isPrompt && dropdownField === "description" && (
          <TemplateTextField
            value={action.value as string}
            onChange={(value) => onChange({ field: "description", value })}
            inputNames={templateInputs}
          />
        )}
        {!isPrompt && dropdownField === "amount" && (
          <AmountActionField
            value={action.value as Money}
            onChange={(value) => onChange({ field: "amount", value })}
          />
        )}
        {!isPrompt && dropdownField === "datePaid" && (
          <DatePicker
            mode="single"
            value={action.value as DateString}
            onChange={(v) => {
              if (v) {
                onChange({ field: "datePaid", value: makeDateString(v) });
              }
            }}
            fullWidth
          />
        )}
      </Box>
      <IconButton
        onClick={onRemove}
        size="small"
        sx={{ alignSelf: isMobile ? "flex-end" : "auto" }}
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );
};
